/**
 * PATH: packages/ui/src/shortcuts/registry.ts
 * FILE_ID: UI-SHORTCUTS-REGISTRY-001
 * PURPOSE: Single source of truth for every keyboard shortcut across all
 *          Quotech hubs, grouped by hub so the Settings → Shortcuts page can
 *          render one per-hub-tabbed reference instead of N scattered modals.
 * WHY: Operators complained "there are a lot of shortcuts across different
 *      hubs" — they were defined independently in each hub's own help modal
 *      (apps/crm, apps/quote, apps/qdrive) and the Kanban/Email inline
 *      handlers, with nothing aggregated. This registry consolidates them.
 *
 * FLOW:
 *   ┌──────────────────┐    ┌─────────────────────┐    ┌──────────────────┐
 *   │ hub source code   │ → │ SHORTCUT_REGISTRY    │ → │ HubShortcutsRef    │
 *   │ (keydown handlers)│    │ (this file, by hub) │    │ (per-hub tabs)     │
 *   └──────────────────┘    └─────────────────────┘    └──────────────────┘
 *
 * EVIDENCE (each section mirrors the real handler/modal it documents):
 *   - general: packages/settings-hub SHORTCUTS + packages/ui KeyboardShortcutsTab
 *   - email:   packages/email-hub/.../InboxPage.tsx keydown handler
 *   - kanban:  packages/ui/src/kanban/components/KanbanBoardView.tsx keydown switch
 *   - quote:   apps/quote/.../modals/KeyboardShortcutsModal.tsx SHORTCUTS
 *   - crm:     apps/crm/.../ui/KeyboardShortcutsModal.tsx SHORTCUT_GROUPS
 *   - qdrive:  apps/qdrive/.../ui/KeyboardShortcutOverlay.tsx SHORTCUT_GROUPS
 *
 * RELATED:
 *   - ./search.ts: pure filter function used by the search box
 *   - ../components/settings/HubShortcutsReference.tsx: the consumer UI
 */

// WP5 2026-07-05 — the kanban section is DERIVED from the kanban shortcut
// registry (the same table the board's keydown handler and `?` cheat sheet
// consume), so this reference can never drift from the real handler again.
// Previously it was a hand-maintained mirror missing priority/pin/undo rows.
import { KANBAN_SHORTCUTS } from '../kanban/shortcuts/registry'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Stable id for each hub tab. Persisted as the user's selected-tab pref. */
export type ShortcutHubId =
  | 'general'
  | 'email'
  | 'kanban'
  | 'quote'
  | 'crm'
  | 'qdrive'

export interface ShortcutEntry {
  /** Keys to display, already normalised (e.g. ['⌘/Ctrl', 'K']). */
  keys: string[]
  /** What the shortcut does. */
  description: string
  /** Optional sub-grouping within a hub (e.g. 'Navigation', 'Editing'). */
  category?: string
}

export interface ShortcutHubSection {
  id: ShortcutHubId
  /** Tab label shown in the UI. */
  label: string
  /** Short tagline describing the hub context. */
  hint: string
  shortcuts: ShortcutEntry[]
}

/** The mod key is rendered cross-platform so one row covers macOS + Win/Linux. */
const MOD = '⌘/Ctrl'

/** f(win display string) -> KeyCap parts (['Shift','F'], ['⌘/Ctrl','Z']). */
function splitWinDisplay(win: string): string[] {
  return win.split(' + ').map((part) => (part === 'Ctrl' ? MOD : part))
}

const KANBAN_SECTION_SHORTCUTS: ShortcutEntry[] = KANBAN_SHORTCUTS.map((def) => ({
  keys: splitWinDisplay(def.defaultKeys.win),
  description: def.label,
  category: def.category,
}))

// ---------------------------------------------------------------------------
// Registry data (grouped by hub)
// ---------------------------------------------------------------------------

export const SHORTCUT_REGISTRY: ShortcutHubSection[] = [
  {
    id: 'general',
    label: 'General',
    hint: 'Work everywhere across Quotech',
    shortcuts: [
      { keys: [MOD, ','], description: 'Open or close settings', category: 'App' },
      { keys: [MOD, 'B'], description: 'Toggle the sidebar', category: 'App' },
      { keys: [MOD, 'K'], description: 'Open the command palette', category: 'App' },
      { keys: [MOD, 'F'], description: 'Global search', category: 'App' },
      { keys: ['?'], description: 'Show keyboard shortcuts (where available)', category: 'App' },
      { keys: ['Escape'], description: 'Close dialogs, sheets, or cancel the current action', category: 'App' },
      { keys: ['↑', '↓'], description: 'Move through command palette / list results', category: 'Navigation' },
      { keys: ['Enter'], description: 'Run the selected command or confirm the focused action', category: 'Navigation' },
      { keys: ['Tab strip ↺'], description: 'Return to last opened tab (icon just left of Home) — tab positions stay put', category: 'Navigation' },
      { keys: [MOD, 'E'], description: 'Jump to the Email Hub — right-click any hub in the sidebar to set or change its shortcut', category: 'Navigation' },
      { keys: [MOD, 'S'], description: 'Save (contextual — e.g. Quote Builder)', category: 'Actions' },
      { keys: [MOD, 'Enter'], description: 'Commit inline edits (contextual)', category: 'Actions' },
    ],
  },
  {
    id: 'email',
    label: 'Email Hub',
    hint: 'Triage the inbox without the mouse',
    // EVIDENCE: each row mirrors a real handler — inbox keys in
    // packages/email-hub/.../useInboxHotkeys.ts (registry-driven, see
    // packages/email-hub/.../shortcuts/registry.ts), reader keys in
    // EmailPreviewPane.tsx / EmailReader.tsx, markup in
    // useGlobalMarkupShortcut.ts. Rewritten 2026-07-06
    // (FEAT-2026-07-06-EMAIL-SHORTCUTS): `c` is now COMPLETE (done + flips the
    // linked card + hides), plain archive moved to `e`, and the set gained
    // n / / / u / p / 1-3 / mod+X / ?. A drift test in email-hub pins this
    // section against the canonical EMAIL_SHORTCUTS table.
    shortcuts: [
      { keys: ['↓'], description: 'Move to the next email', category: 'Navigation' },
      { keys: ['↑'], description: 'Move to the previous email', category: 'Navigation' },
      { keys: ['Enter'], description: 'Open the selected email', category: 'Navigation' },
      { keys: ['Escape'], description: 'Deselect the current email', category: 'Navigation' },
      { keys: ['n'], description: 'Compose a new email', category: 'Navigation' },
      { keys: ['/'], description: 'Focus the email search', category: 'Navigation' },
      { keys: ['c'], description: 'Complete — mark done (flips the linked card) and hide from view', category: 'Triage' },
      { keys: ['e'], description: 'Archive the hovered / selected email', category: 'Triage' },
      { keys: ['d'], description: 'Done — archive the selected email(s)', category: 'Triage' },
      { keys: ['j'], description: 'Junk — move the selected email(s) to junk', category: 'Triage' },
      { keys: [MOD, 'X'], description: 'Delete — move to the bin', category: 'Triage' },
      { keys: ['u'], description: 'Mark read / unread', category: 'Triage' },
      { keys: ['t'], description: 'Quick Triage — WHO / WHAT / SIGNALS / WHERE palette', category: 'Triage' },
      { keys: ['a'], description: 'Allocate the hovered / selected email to a Kanban board', category: 'Organise' },
      { keys: ['p'], description: 'Pin / unpin (star) the email', category: 'Organise' },
      { keys: ['1'], description: 'Set the linked card to Normal priority', category: 'Organise' },
      { keys: ['2'], description: 'Set the linked card to High priority', category: 'Organise' },
      { keys: ['3'], description: 'Set the linked card to Urgent priority', category: 'Organise' },
      { keys: ['j'], description: 'Next email (in the open email)', category: 'In the open email' },
      { keys: ['k'], description: 'Previous email (in the open email)', category: 'In the open email' },
      { keys: ['A'], description: 'Allocate this email to a Kanban board', category: 'In the open email' },
      { keys: ['D'], description: 'Done — archive and mark the linked card done', category: 'In the open email' },
      { keys: ['B'], description: 'Open the board this email is allocated to', category: 'In the open email' },
      { keys: ['@'], description: 'Ping / assign a teammate', category: 'In the open email' },
      { keys: [MOD, 'Shift', 'M'], description: 'Paste a screenshot from the clipboard and mark it up', category: 'In the open email' },
      { keys: ['?'], description: 'Show / hide keyboard shortcuts', category: 'Help' },
      { keys: [MOD, 'F'], description: 'Search emails', category: 'Search' },
    ],
  },
  {
    id: 'kanban',
    label: 'Kanban',
    hint: 'Drive the board from the keyboard — customise these on the board via ? → click a key',
    shortcuts: KANBAN_SECTION_SHORTCUTS,
  },
  {
    id: 'quote',
    label: 'Quote Builder',
    hint: 'Spreadsheet-style estimating',
    shortcuts: [
      { keys: ['↑', '↓', '←', '→'], description: 'Move between cells', category: 'Navigation' },
      { keys: ['Tab'], description: 'Move to next cell', category: 'Navigation' },
      { keys: ['Shift', 'Tab'], description: 'Move to previous cell', category: 'Navigation' },
      { keys: ['Home'], description: 'Go to first cell in row', category: 'Navigation' },
      { keys: ['End'], description: 'Go to last cell in row', category: 'Navigation' },
      { keys: ['Ctrl', 'G'], description: 'Go to row/cell', category: 'Navigation' },
      { keys: ['Enter'], description: 'Start editing / confirm and move down', category: 'Editing' },
      { keys: ['F2'], description: 'Edit current cell', category: 'Editing' },
      { keys: ['Escape'], description: 'Cancel editing', category: 'Editing' },
      { keys: ['Delete'], description: 'Clear cell content', category: 'Editing' },
      { keys: ['Ctrl', 'Z'], description: 'Undo last change', category: 'Editing' },
      { keys: ['Ctrl', 'Y'], description: 'Redo last change', category: 'Editing' },
      { keys: ['Ctrl', 'A'], description: 'Select all rows in section', category: 'Selection' },
      { keys: ['Shift', 'Space'], description: 'Select entire row', category: 'Selection' },
      { keys: ['Ctrl', 'Space'], description: 'Select entire column', category: 'Selection' },
      { keys: ['Ctrl', 'C'], description: 'Copy selection', category: 'Clipboard' },
      { keys: ['Ctrl', 'X'], description: 'Cut selection', category: 'Clipboard' },
      { keys: ['Ctrl', 'V'], description: 'Paste', category: 'Clipboard' },
      { keys: ['Ctrl', 'D'], description: 'Fill down from first row', category: 'Clipboard' },
      { keys: ['Ctrl', 'R'], description: 'Fill right from first column', category: 'Clipboard' },
      { keys: ['Alt', 'S'], description: 'Add new section', category: 'Actions' },
      { keys: ['Alt', 'R'], description: 'Add new row', category: 'Actions' },
      { keys: ['Ctrl', 'S'], description: 'Save quote', category: 'Actions' },
      { keys: ['Ctrl', 'E'], description: 'Export to Excel', category: 'Actions' },
      { keys: ['F9'], description: 'Recalculate totals', category: 'Actions' },
      { keys: ['Alt', '1'], description: 'Toggle sections panel', category: 'Panels' },
      { keys: ['Ctrl', 'I'], description: 'Toggle inspector', category: 'Panels' },
      { keys: ['Ctrl', 'B'], description: 'Toggle AI assistant', category: 'Panels' },
      { keys: ['Ctrl', '/'], description: 'Open command palette', category: 'Panels' },
    ],
  },
  {
    id: 'crm',
    label: 'CRM',
    hint: 'Pipeline & quotes navigation',
    shortcuts: [
      { keys: [MOD, 'K'], description: 'Open command palette', category: 'Navigation' },
      { keys: [MOD, '1'], description: 'Go to Dashboard', category: 'Navigation' },
      { keys: [MOD, '2'], description: 'Go to Pipeline', category: 'Navigation' },
      { keys: [MOD, '3'], description: 'Go to Quotes', category: 'Navigation' },
      { keys: [MOD, '4'], description: 'Go to Analytics', category: 'Navigation' },
      { keys: [MOD, 'N'], description: 'Create new quote', category: 'Actions' },
      { keys: [MOD, '/'], description: 'Focus search', category: 'Actions' },
      { keys: ['?'], description: 'Show keyboard shortcuts', category: 'Actions' },
      { keys: ['Escape'], description: 'Close modal / cancel', category: 'Actions' },
    ],
  },
  {
    id: 'qdrive',
    label: 'QDrive',
    hint: 'File browsing like a desktop',
    shortcuts: [
      { keys: ['↑', '↓'], description: 'Move focus up / down', category: 'Navigation' },
      { keys: ['←', '→'], description: 'Collapse / expand folder', category: 'Navigation' },
      { keys: ['Home'], description: 'Jump to first item', category: 'Navigation' },
      { keys: ['End'], description: 'Jump to last item', category: 'Navigation' },
      { keys: ['Enter'], description: 'Open file or folder', category: 'Navigation' },
      { keys: ['Backspace'], description: 'Go up one folder', category: 'Navigation' },
      { keys: ['Tab'], description: 'Move to next pane', category: 'Navigation' },
      { keys: ['Shift', 'Click'], description: 'Range select', category: 'Selection' },
      { keys: [MOD, 'Click'], description: 'Toggle select', category: 'Selection' },
      { keys: [MOD, 'A'], description: 'Select all', category: 'Selection' },
      { keys: ['Escape'], description: 'Deselect all', category: 'Selection' },
      { keys: [MOD, 'D'], description: 'Download selected', category: 'File operations' },
      { keys: [MOD, 'C'], description: 'Copy file reference', category: 'File operations' },
      { keys: ['Delete'], description: 'Move to trash', category: 'File operations' },
      { keys: ['F2'], description: 'Rename file', category: 'File operations' },
      { keys: [MOD, 'Shift', 'N'], description: 'New folder', category: 'File operations' },
      { keys: [MOD, 'Z'], description: 'Undo last action', category: 'File operations' },
      { keys: [MOD, '1'], description: 'List view', category: 'Views' },
      { keys: [MOD, '2'], description: 'Grid view', category: 'Views' },
      { keys: [MOD, '3'], description: 'Column view', category: 'Views' },
      { keys: ['Space'], description: 'Quick Look (preview)', category: 'Views' },
      { keys: [MOD, 'I'], description: 'Toggle info panel', category: 'Views' },
    ],
  },
]

/** Tab order = registry order; exported so the UI and tests agree on it. */
export const SHORTCUT_HUB_IDS: ShortcutHubId[] = SHORTCUT_REGISTRY.map((s) => s.id)

/** Default tab when the user has no saved preference. */
export const DEFAULT_SHORTCUT_HUB: ShortcutHubId = 'general'

/**
 * getShortcutSection — id → section (or undefined). Pure lookup.
 */
export function getShortcutSection(
  id: ShortcutHubId,
): ShortcutHubSection | undefined {
  return SHORTCUT_REGISTRY.find((s) => s.id === id)
}

/**
 * isShortcutHubId — narrow an arbitrary string to a known hub id. Used when
 * hydrating the persisted preference (which could be stale/garbage).
 */
export function isShortcutHubId(value: unknown): value is ShortcutHubId {
  return typeof value === 'string' && SHORTCUT_HUB_IDS.includes(value as ShortcutHubId)
}
